import { Fragment, useMemo } from "react"
import type React from "react"

import { format } from "date-fns"

import { resolveRouteKind, segmentLoadStates } from "@/lib/routeCostCalculation"
import { getRouteKindLaneClasses } from "@/lib/routeKindDisplay"
import { ROW_HEIGHT } from "@/lib/timelineScale"
import { cn } from "@/lib/utils"
import type { PlannedRoute, PlannedStop } from "@/types/collections"

import { HaulageSellableMarker } from "./HaulageSellableMarker"
import { PlanningTimelineStop } from "./PlanningTimelineStop"
import type { DragCreateState, DragType } from "./types"

export function PlanningTimelineRow({
  route,
  rowIndex,
  isFirstStandby,
  isHighlighted,
  isDropTarget,
  readOnly,
  dragCreatePreview,
  filterHaulageApplicable,
  driverMaxHours,
  selectedDate,
  draggingStopId,
  getTimePosition,
  getVolumeForecastForStop,
  onDragStart,
  onSlotDragOver,
  onSlotDragLeave,
  onSlotDrop,
  onStopDragStart,
  onDeleteStop,
  onUpdateStopMoney,
}: {
  route: PlannedRoute
  rowIndex: number
  isFirstStandby: boolean
  isHighlighted: boolean
  isDropTarget: boolean
  readOnly: boolean
  dragCreatePreview: DragCreateState | null
  filterHaulageApplicable: boolean
  driverMaxHours: number
  selectedDate: Date
  draggingStopId: string | undefined
  getTimePosition: (time: Date) => number
  getVolumeForecastForStop: (route: PlannedRoute, stop: { locationId?: string }) => number
  onDragStart: (e: React.MouseEvent, rowIndex: number) => void
  onSlotDragOver: (e: React.DragEvent, rowIndex: number) => void
  onSlotDragLeave: (e: React.DragEvent) => void
  onSlotDrop: (e: React.DragEvent, rowIndex: number) => void
  onStopDragStart: (
    e: React.MouseEvent,
    stop: PlannedStop,
    route: PlannedRoute,
    type: DragType,
  ) => void
  onDeleteStop: (routeId: string, stopId: string) => void
  onUpdateStopMoney: (
    routeId: string,
    stopId: string,
    field: "haulageDeliveryRevenueGbp" | "directInjectionCostGbp",
    value: number | undefined,
  ) => void
}) {
  // What each leg carries, in stop order: legStates[i - 1] is the leg into stop i.
  const legStates = useMemo(
    () =>
      segmentLoadStates(route.stops, (i) => getVolumeForecastForStop(route, route.stops[i]) || 0),
    [route, getVolumeForecastForStop],
  )

  return (
    <div
      className={cn(
        "relative hover:bg-palette-jungleGreen000/50 border-b border-palette-jungleGreen100 transition-colors",
        !readOnly && "cursor-crosshair",
        getRouteKindLaneClasses(resolveRouteKind(route.route_kind)),
        isFirstStandby && "border-t-2 border-t-slate-400",
        isHighlighted && "bg-amber-100/30",
        isDropTarget &&
          "bg-blue-100/50 ring-2 ring-inset ring-blue-400",
      )}
      style={{ height: `${ROW_HEIGHT}px` }}
      onMouseDown={readOnly ? undefined : (e) => onDragStart(e, rowIndex)}
      onDragOver={readOnly ? undefined : (e) => onSlotDragOver(e, rowIndex)}
      onDragLeave={readOnly ? undefined : (e) => onSlotDragLeave(e)}
      onDrop={readOnly ? undefined : (e) => onSlotDrop(e, rowIndex)}
    >
      {dragCreatePreview && (
        <div
          className="absolute top-2 bottom-2 rounded border-2 border-dashed border-blue-400 bg-blue-100/50 z-20"
          style={{
            left: `${getTimePosition(dragCreatePreview.startTime)}px`,
            width: `${getTimePosition(dragCreatePreview.endTime) - getTimePosition(dragCreatePreview.startTime)}px`,
            minWidth: "20px",
          }}
        >
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-[10px] text-blue-600 font-medium bg-white/80 px-1 rounded">
              {format(dragCreatePreview.startTime, "HH:mm")} -{" "}
              {format(dragCreatePreview.endTime, "HH:mm")}
            </span>
          </div>
        </div>
      )}

      {filterHaulageApplicable && (
        <HaulageSellableMarker
          route={route}
          driverMaxHours={driverMaxHours}
          selectedDate={selectedDate}
          getTimePosition={getTimePosition}
        />
      )}

      {route.stops.map((stop, index) => {
        const startPos = getTimePosition(stop.plannedArrival)
        const endPos = getTimePosition(stop.plannedDeparture)
        const width = Math.max(endPos - startPos, 30)

        return (
          <Fragment key={stop.id}>
            {index > 0 &&
              (legStates[index - 1] === "internal" ? (
                <div
                  className="absolute top-1/2 h-0 -translate-y-1/2 z-0 border-t-[4px] border-dashed border-palette-jungleGreen400"
                  style={{
                    left: `${getTimePosition(route.stops[index - 1].plannedDeparture)}px`,
                    width: `${startPos - getTimePosition(route.stops[index - 1].plannedDeparture)}px`,
                  }}
                />
              ) : (
                <div
                  className={cn(
                    "absolute top-1/2 h-1 -translate-y-1/2 z-0 rounded-full",
                    legStates[index - 1] === "empty"
                      ? "bg-red-200"
                      : "bg-palette-jungleGreen300",
                  )}
                  style={{
                    left: `${getTimePosition(route.stops[index - 1].plannedDeparture)}px`,
                    width: `${startPos - getTimePosition(route.stops[index - 1].plannedDeparture)}px`,
                  }}
                />
              ))}

            <PlanningTimelineStop
              route={route}
              stop={stop}
              startPos={startPos}
              width={width}
              isDragging={draggingStopId === stop.id}
              readOnly={readOnly}
              getVolumeForecastForStop={getVolumeForecastForStop}
              onStopDragStart={onStopDragStart}
              onDeleteStop={onDeleteStop}
              onUpdateStopMoney={onUpdateStopMoney}
            />
          </Fragment>
        )
      })}
    </div>
  )
}
